import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import JoditEditor from "jodit-react";
import Handler from "../../../Helpers/Handler";
import { textCapitalize } from "../../../Helpers/Utils";
import { htmlTextLength } from "../../../Helpers/bookingRules";

export const DOCUMENT_SITES = [
    { key: "customer", name: "Customer" },
    // { key: "staff", name: "Staff" },
    // { key: "admin", name: "Admin" },
    { key: "associate", name: "Associate" },
];

export const DOCUMENT_STATUSES = [
    { key: "draft", name: "Draft", tone: "text-bg-secondary" },
    { key: "pending", name: "Pending", tone: "text-bg-warning" },
    { key: "published", name: "Published", tone: "text-bg-success" },
];

const NAME_MAX = 100;
const DESCRIPTION_MAX = 2000;
const CONTENT_MAX = 200000;
const SEO_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const NOTE_MAX = 200;
const TYPE_MAX = 50;
const TYPE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const TYPE_MESSAGE = "Use lowercase letters, numbers and single hyphens, with no spaces";

const slugify = (text) => String(text || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);
const emptyForm = () => ({ documentName: "", documentType: "", site: ["customer"], documentDescription: "", authorName: "", seo_name: "", footerGroup: "", footerOrder: 0, footerLabel: "", version: "", documentUrl: "", changeNote: "" });

const JODIT_CONFIG = {
    readonly: false,
    height: 420,
    placeholder: "Write the document here...",
    toolbarAdaptive: false,
    buttons: [
        "paragraph", "bold", "italic", "underline", "strikethrough", "|",
        "ul", "ol", "|",
        "align", "|",
        "link", "table", "hr", "|",
        "undo", "redo", "|",
        "source",
    ],
};

const DocumentFormModal = ({ documentId, onClose, onSaved }) => {
    const modalRef = useRef(null);
    const savedRef = useRef(false);
    const originalRef = useRef("");
    const typeTouchedRef = useRef(Boolean(documentId));
    const mode = documentId ? "edit" : "create";
    const [form, setForm] = useState(emptyForm());
    const [loading, setLoading] = useState(Boolean(documentId));
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const joditConfig = useMemo(() => JODIT_CONFIG, []);

    const setField = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: null }));
    };

    const onNameChange = (value) => {
        setForm((f) => ({ ...f, documentName: value, ...(typeTouchedRef.current ? {} : { documentType: slugify(value).slice(0, TYPE_MAX) }) }));
        setErrors((e) => ({ ...e, documentName: null, ...(typeTouchedRef.current ? {} : { documentType: null }) }));
    };

    const onTypeChange = (value) => {
        typeTouchedRef.current = value !== "";
        setForm((f) => ({ ...f, documentType: value }));
        setErrors((e) => ({ ...e, documentType: value && !TYPE_PATTERN.test(value) ? TYPE_MESSAGE : null }));
    };

    useEffect(() => {
        const el = modalRef.current;
        if (!el || !window.bootstrap) return undefined;
        const instance = window.bootstrap.Modal.getOrCreateInstance(el, { backdrop: "static" });
        const handleHidden = () => (savedRef.current ? onSaved?.() : onClose?.());
        el.addEventListener("hidden.bs.modal", handleHidden);
        instance.show();
        return () => {
            el.removeEventListener("hidden.bs.modal", handleHidden);
            instance.dispose();
            document.querySelectorAll(".modal-backdrop").forEach((node) => node.remove());
            document.body.classList.remove("modal-open");
            document.body.style.removeProperty("overflow");
            document.body.style.removeProperty("padding-right");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!documentId) return;
        (async () => {
            const response = await Handler({ method: "get", url: `/admin/document/${documentId}` });
            const latest = response?.success ? response?.data?.data?.latestVersion || response?.data?.data?.version : null;
            const versionResponse = latest ? await Handler({ method: "get", url: `/admin/document/${documentId}/versions/${latest}` }) : null;
            if (response?.success && versionResponse?.success) {
                const data = { ...(response?.data?.data || {}), version: latest, documentUrl: versionResponse?.data?.data?.documentUrl || "" };
                setForm({
                    documentName: data.documentName || "",
                    documentType: data.documentType || "",
                    site: (data.site || []).slice(0, 1),
                    documentDescription: data.documentDescription || "",
                    authorName: data.authorName || "",
                    seo_name: data.seo_name || "",
                    footerGroup: data.footerGroup || "",
                    footerOrder: Number(data.footerOrder) || 0,
                    footerLabel: data.footerLabel || "",
                    version: data.version || "1.0",
                    documentUrl: data.documentUrl || "",
                    changeNote: "",
                });
                originalRef.current = (data.documentUrl || "").trim();
            } else {
                toast.error(textCapitalize((versionResponse?.success === false ? versionResponse?.data?.message : response?.data?.message) || "Unable to load this document"));
                window.bootstrap?.Modal.getInstance(modalRef.current)?.hide();
            }
            setLoading(false);
        })();
    }, [documentId]);

    const onSubmit = async () => {
        const name = form.documentName.trim();
        const seo = form.seo_name.trim();
        const type = form.documentType;
        const next = {
            documentName: !name ? "Name is required" : name.length > NAME_MAX ? `Name can be at most ${NAME_MAX} characters` : null,
            documentType: !type ? "Type is required" : type.length > TYPE_MAX ? `Type can be at most ${TYPE_MAX} characters` : !TYPE_PATTERN.test(type) ? TYPE_MESSAGE : null,
            site: form.site.length ? null : "Choose a site",
            documentDescription: !form.documentDescription.trim() ? "Description is required" : form.documentDescription.length > DESCRIPTION_MAX ? `Description can be at most ${DESCRIPTION_MAX} characters` : null,
            authorName: !form.authorName.trim() ? "Author is required" : form.authorName.length > NAME_MAX ? `Author can be at most ${NAME_MAX} characters` : null,
            seo_name: seo && !SEO_PATTERN.test(seo) ? "Use lowercase letters, numbers and single hyphens" : null,
            changeNote: form.changeNote.length > NOTE_MAX ? `Note can be at most ${NOTE_MAX} characters` : null,
            documentUrl: !htmlTextLength(form.documentUrl) ? "Content is required" : form.documentUrl.length > CONTENT_MAX ? `Content can be at most ${CONTENT_MAX} characters` : null,
        };
        setErrors(next);
        const problem = Object.values(next).find(Boolean);
        if (problem) { toast.error(problem); return; }
        const payload = {
            documentName: name,
            documentType: type,
            site: form.site,
            documentDescription: form.documentDescription.trim(),
            authorName: form.authorName.trim(),
            ...(seo ? { seo_name: seo } : {}),
            ...(form.site[0] === "customer" ? { footerGroup: form.footerGroup, footerOrder: Number(form.footerOrder) || 0, footerLabel: form.footerLabel.trim() } : {}),
            ...(mode === "create" || contentChanged ? { documentUrl: form.documentUrl } : {}),
            ...(mode === "edit" && contentChanged && form.changeNote.trim() ? { changeNote: form.changeNote.trim() } : {}),
        };
        setSaving(true);
        const response = mode === "create"
            ? await Handler({ method: "post", url: "/admin/document", data: payload })
            : await Handler({ method: "patch", url: `/admin/document/${documentId}`, data: payload });
        setSaving(false);
        const message = response?.data?.message;
        if (response?.success || message === "Already up to date") {
            if (response?.success) toast.success(message || "Saved successfully"); else toast.info(message);
            savedRef.current = true;
            onSaved?.();
        } else {
            toast.error(textCapitalize(message || "Unable to save this document"));
        }
    };

    const contentChanged = mode === "edit" && form.documentUrl.trim() !== originalRef.current;
    const seoPreview = form.seo_name.trim() || slugify(form.documentName);

    return createPortal(
        <div className="modal fade" tabIndex="-1" ref={modalRef} aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
                <div className="modal-content border-0 rounded-4">
                    <div className="modal-header">
                        <h5 className="modal-title fw-semibold">{mode === "create" ? "New document" : "Edit document"}</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        {loading ? <div className="text-muted py-5 text-center">Loading...</div> : (
                            <div className="row g-3">
                                <div className="col-12 col-md-6">
                                    <label className="form-label small text-muted mb-1" htmlFor="document-name">Name</label>
                                    <input autoComplete="off" id="document-name" type="text" maxLength={NAME_MAX} className={`form-control${errors.documentName ? " is-invalid" : ""}`} value={form.documentName} onChange={(e) => onNameChange(e.target.value)} placeholder="e.g. Privacy Policy" />
                                    {errors.documentName && <div className="invalid-feedback">{errors.documentName}</div>}
                                </div>
                                <div className="col-6 col-md-6">
                                    <label className="form-label small text-muted mb-1" htmlFor="document-type">Type</label>
                                    <input id="document-type" type="text" maxLength={TYPE_MAX} autoComplete="off" className={`form-control${errors.documentType ? " is-invalid" : ""}`} value={form.documentType} onChange={(e) => onTypeChange(e.target.value)} placeholder="e.g. privacy-policy" />
                                    {errors.documentType && <div className="invalid-feedback">{errors.documentType}</div>}
                                </div>
                                

                                <div className="col-12 col-md-6">
                                    <label className="form-label small text-muted mb-1" htmlFor="document-author">Author</label>
                                    <input autoComplete="off" id="document-author" type="text" maxLength={NAME_MAX} className={`form-control${errors.authorName ? " is-invalid" : ""}`} value={form.authorName} onChange={(e) => setField("authorName", e.target.value)} placeholder="e.g. Legal team" />
                                    {errors.authorName && <div className="invalid-feedback">{errors.authorName}</div>}
                                </div>
                                <div className="col-12 col-md-6">
                                    <label className="form-label small text-muted mb-1" htmlFor="document-seo">Link name (seo_name)</label>
                                    <input autoComplete="off" id="document-seo" type="text" className={`form-control${errors.seo_name ? " is-invalid" : ""}`} value={form.seo_name} onChange={(e) => setField("seo_name", e.target.value.toLowerCase())} placeholder={slugify(form.documentName) || "made from the name"} />
                                    {errors.seo_name ? <div className="invalid-feedback">{errors.seo_name}</div> : (
                                        <div className="form-text">{mode === "create" ? "Leave blank to make it from the name" : "Changing it changes the public link"}: <span className="font-monospace">{seoPreview || "-"}</span></div>
                                    )}
                                </div>

                                <div className="col-12">
                                    <div className="form-label small text-muted mb-1">Shown on</div>
                                    <div className="d-flex flex-wrap gap-3" role="radiogroup" aria-label="Site">
                                        {DOCUMENT_SITES.map((s) => (
                                            <div className="form-check" key={s.key}>
                                                <input autoComplete="off" id={`document-site-${s.key}`} type="radio" name="document-site" className="form-check-input" checked={form.site[0] === s.key} onChange={() => setField("site", [s.key])} />
                                                <label className="form-check-label" htmlFor={`document-site-${s.key}`}>{s.name}</label>
                                            </div>
                                        ))}
                                    </div>
                                    {errors.site && <div className="text-danger small mt-1" role="alert">{errors.site}</div>}
                                </div>

                                {form.site[0] === "customer" && (
                                    <>
                                        <div className="col-12 col-md-4">
                                            <label className="form-label small text-muted mb-1" htmlFor="document-footer-group">Footer column</label>
                                            <select id="document-footer-group" className="form-select" value={form.footerGroup} onChange={(e) => setField("footerGroup", e.target.value)}>
                                                <option value="">Not in the footer</option>
                                                <option value="Company">Company</option>
                                                <option value="Services">Services</option>
                                                <option value="Legal">Legal</option>
                                            </select>
                                        </div>
                                        <div className="col-6 col-md-3">
                                            <label className="form-label small text-muted mb-1" htmlFor="document-footer-order">Footer order</label>
                                            <input id="document-footer-order" type="number" min={0} max={999} className="form-control" value={form.footerOrder} disabled={!form.footerGroup} onChange={(e) => setField("footerOrder", e.target.value)} />
                                        </div>
                                        <div className="col-12 col-md-5">
                                            <label className="form-label small text-muted mb-1" htmlFor="document-footer-label">Footer label (optional)</label>
                                            <input autoComplete="off" id="document-footer-label" type="text" maxLength={60} className="form-control" value={form.footerLabel} disabled={!form.footerGroup} onChange={(e) => setField("footerLabel", e.target.value)} placeholder="The document name is used when empty" />
                                        </div>
                                        {form.footerGroup && <div className="col-12 form-text mt-0">Shown in the website footer once the document is published, as a link to <span className="font-monospace">/{seoPreview || "-"}</span>.</div>}
                                    </>
                                )}

                                <div className="col-12">
                                    <label className="form-label small text-muted mb-1" htmlFor="document-description">Description</label>
                                    <textarea id="document-description" rows={3} className={`form-control${errors.documentDescription ? " is-invalid" : ""}`} value={form.documentDescription} onChange={(e) => setField("documentDescription", e.target.value)} placeholder="A short summary of what this document covers" />
                                    <div className="d-flex justify-content-between">
                                        {errors.documentDescription ? <div className="text-danger small">{errors.documentDescription}</div> : <span />}
                                        <span className="form-text">{form.documentDescription.length}/{DESCRIPTION_MAX}</span>
                                    </div>
                                </div>
                                <div className="col-6 col-md-2">
                                    <span className="form-label small text-muted">Version</span>
                                    <span className="form-control-plaintext fw-semibold d-inline ms-2">{mode === "create" ? "1.0" : `${form.version} (latest)`}</span>
                                </div>
                                <div className="col-12">
                                    <label className="form-label small text-muted mb-1">Document</label>
                                    <JoditEditor value={form.documentUrl} config={joditConfig} onBlur={(content) => setField("documentUrl", content)} />
                                    {errors.documentUrl && <div className="text-danger small mt-1" role="alert">{errors.documentUrl}</div>}
                                    <div className="form-text">{mode === "create" ? "Saved as version 1.0 (draft)." : "Changing the text saves a new draft version. The published text stays as it is until you publish the new version from History."}</div>
                                </div>
                                {contentChanged ? (
                                    <div className="col-12">
                                        <label className="form-label small text-muted mb-1" htmlFor="document-note">What changed <span className="text-muted">(optional)</span></label>
                                        <input id="document-note" autoComplete="off" type="text" maxLength={NOTE_MAX} className={`form-control${errors.changeNote ? " is-invalid" : ""}`} value={form.changeNote} onChange={(e) => setField("changeNote", e.target.value)} placeholder="e.g. Updated the refund window to 7 days" />
                                        {errors.changeNote && <div className="invalid-feedback">{errors.changeNote}</div>}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={saving || loading}>{saving ? "Saving..." : "Save"}</button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DocumentFormModal;
